import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Merge, Download, ArrowLeft, Plus, X, ArrowUp, ArrowDown, Upload, FileText, Shield, Zap, Files, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import apiClient from '../lib/api';
import { getSessionId } from '../lib/session';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get or create session ID
const MergePage = () => {
  const { toast } = useToast();
  const [uploadedPdfs, setUploadedPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('merge-pdf-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper function to get PDF page count
  const getPdfPageCount = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      return pdf.numPages;
    } catch (error) {
      console.error('Error reading PDF:', error);
      return 0;
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  };

  const handleFileUpload = async (files) => {
    const pdfFiles = Array.from(files).filter(file => file.name.toLowerCase().endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast({
        title: "Error",
        description: "Please upload PDF files only",
        variant: "destructive",
      });
      return;
    }

    const oversizedFiles = pdfFiles.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast({
        title: "Error",
        description: `File(s) too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum 10MB per file.`,
        variant: "destructive",
      });
      return;
    }

    setUploadingFiles(true);
    
    try {
      const newPdfs = [];
      
      for (let i = 0; i < pdfFiles.length; i++) {
        const file = pdfFiles[i];
        const pageCount = await getPdfPageCount(file);
        
        // Upload to backend
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await apiClient.post(
          `${BACKEND_URL}/api/pdf/upload?session_id=${sessionId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        
        newPdfs.push({
          id: uploadResponse.data.id,
          file: file,
          name: file.name,
          size: uploadResponse.data.size,
          pages: uploadResponse.data.pages,
          uploadDate: uploadResponse.data.upload_date,
          preview: URL.createObjectURL(file)
        });
      }

      setUploadedPdfs(prev => [...prev, ...newPdfs]);
      setMergeResult(null);
      
      toast({
        title: "Success",
        description: `${newPdfs.length} PDF(s) uploaded successfully`,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload PDF files",
        variant: "destructive",
      });
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleRemovePdf = (pdfId) => {
    setUploadedPdfs(prev => {
      const pdfToRemove = prev.find(p => p.id === pdfId);
      if (pdfToRemove?.preview) {
        URL.revokeObjectURL(pdfToRemove.preview);
      }
      return prev.filter(p => p.id !== pdfId);
    });
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newOrder = [...uploadedPdfs];
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      setUploadedPdfs(newOrder);
    }
  };

  const handleMoveDown = (index) => {
    if (index < uploadedPdfs.length - 1) {
      const newOrder = [...uploadedPdfs];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setUploadedPdfs(newOrder);
    }
  };

  const handleMerge = async () => {
    if (uploadedPdfs.length < 2) {
      toast({
        title: "Error",
        description: "Please upload at least 2 PDFs to merge",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Get PDF IDs in order
      const pdfIds = uploadedPdfs.map(pdf => pdf.id);
      
      // Call backend merge API
      const response = await apiClient.post(
        `${BACKEND_URL}/api/pdf/merge?session_id=${sessionId}`,
        { pdf_ids: pdfIds }
      );
      
      setMergeResult(response.data);
      
      toast({
        title: "Success",
        description: `${uploadedPdfs.length} PDFs merged successfully`,
      });
    } catch (error) {
      console.error('Merge error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to merge PDFs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!mergeResult) return;
    
    try {
      const response = await apiClient.get(
        `${BACKEND_URL}${mergeResult.download_url}?session_id=${sessionId}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', mergeResult.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Downloaded ${mergeResult.name}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const totalPages = uploadedPdfs.reduce((sum, pdf) => sum + pdf.pages, 0);

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('merge-pdf-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Merge PDF Online — Combine Multiple PDFs Free | AllSmallTooLS</title>
        <meta name="description" content="Easily merge multiple PDF files into one. Free, secure, and fast — combine PDFs right in your browser with no uploads required." />
        <meta name="keywords" content="merge pdf, combine pdfs, pdf joiner, online pdf merger, free pdf combine tool" />
        <link rel="canonical" href="https://allsmalltools.com/merge-pdf" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to PDF merger anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your PDF merger for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
            <Merge className="h-8 w-8 text-blue-500" />
            <span>Merge PDFs</span>
          </h1>
          <p className="text-gray-600">Combine multiple PDF files into a single document</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* PDF Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Upload PDFs to Merge</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                uploadingFiles ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              } ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={uploadingFiles ? undefined : handleDrag}
              onDragLeave={uploadingFiles ? undefined : handleDrag}
              onDragOver={uploadingFiles ? undefined : handleDrag}
              onDrop={uploadingFiles ? undefined : handleDrop}
              onClick={uploadingFiles ? undefined : () => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf"
                onChange={handleFileInput}
                className="hidden"
                disabled={uploadingFiles}
              />
              
              <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${
                dragActive ? 'bg-blue-100' : 'bg-gray-100'
              }`}>
                <Upload className={`h-6 w-6 ${dragActive ? 'text-blue-600' : 'text-gray-400'}`} />
              </div>
              
              <h3 className="font-semibold text-gray-900 mb-2">
                {uploadingFiles ? 'Reading PDFs...' : 'Upload Multiple PDFs'}
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                {uploadingFiles ? 'Please wait while we process your files' : 'Drag and drop PDF files here, or click to browse'}
              </p>
              <p className="text-xs text-gray-500">
                Select multiple files • Max 10MB per file
              </p>
            </div>

            {/* Uploaded Files */}
            {uploadedPdfs.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Uploaded PDFs ({uploadedPdfs.length})</Label>
                  <Badge variant="outline">{totalPages} total pages</Badge>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {uploadedPdfs.map((pdf, index) => (
                    <div key={pdf.id} className="flex items-center space-x-2 bg-gray-50 p-2 rounded">
                      <div className="bg-indigo-100 text-indigo-600 rounded w-6 h-6 flex items-center justify-center text-xs font-semibold">
                        {index + 1}
                      </div>
                      <div className="w-8 h-10 bg-red-100 rounded flex items-center justify-center">
                        <FileText className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{pdf.name}</p>
                        <p className="text-xs text-gray-500">{pdf.pages} pages • {pdf.size}</p>
                      </div>
                      <div className="flex space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === uploadedPdfs.length - 1}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemovePdf(pdf.id)}
                        >
                          <X className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Merge Results */}
        <Card>
          <CardHeader>
            <CardTitle>Merge Results</CardTitle>
          </CardHeader>
          <CardContent>
            {uploadedPdfs.length > 0 ? (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Merge Summary</h4>
                  <div className="space-y-1 text-sm text-blue-600">
                    <p>Files to merge: {uploadedPdfs.length}</p>
                    <p>Total pages: {totalPages}</p>
                    <p>Merge order: {uploadedPdfs.map((pdf, i) => `${i + 1}. ${pdf.name}`).join(', ')}</p>
                  </div>
                </div>

                <Button 
                  onClick={handleMerge}
                  disabled={uploadedPdfs.length < 2 || loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                >
                  {loading ? 'Merging...' : `Merge ${uploadedPdfs.length} PDFs`}
                </Button>

                {mergeResult && (
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-green-800">{mergeResult.name}</p>
                          <p className="text-sm text-green-600">
                            {mergeResult.pages} pages • {mergeResult.size}
                          </p>
                        </div>
                        <Button
                          onClick={handleDownload}
                          size="sm"
                          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Merge className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No PDFs uploaded</p>
                <p className="text-sm">Upload PDFs to start merging</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Why Use Our Merge PDF Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Merge PDF Tool?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Quick & Simple</h3>
              <p className="text-gray-600">
                Merge PDF files in seconds with our intuitive online tool. Upload multiple PDFs, arrange them in order, and combine them into one file instantly. No software download needed.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Secure Processing</h3>
              <p className="text-gray-600">
                Your PDF files are processed securely on our servers. All uploaded and merged files are automatically deleted after processing to ensure your document privacy.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Files className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Unlimited Merging</h3>
              <p className="text-gray-600">
                Combine as many PDF files as you need – no limits on file count or size. Perfect for merging reports, contracts, invoices, or any PDF documents.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Whether you need to combine invoices, merge contracts, or join multiple PDF reports into one document – our free PDF merger makes it easy. Upload your files, arrange them in your preferred order, and download the combined PDF. Try our merge PDF tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/split-pdf" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Split PDF Files
              </Link>
              <Link to="/compress-pdf" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Compress PDF
              </Link>
              <Link to="/lock-unlock-pdf" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Lock/Unlock PDF
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Merge PDF Files Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Merge PDF Files Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Upload PDF Files:</strong> Click the upload area or drag and drop multiple PDF files you want to merge. You can add as many files as needed.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Arrange Order:</strong> Use the up/down arrows to rearrange your PDF files in the desired order. The files will be merged in the order shown.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Merge PDFs:</strong> Click the "Merge PDFs" button. Our tool will combine all your PDF files into a single document in seconds.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download Combined PDF:</strong> Once the merge is complete, download your newly combined PDF file to your device.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Can I merge PDFs for free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our PDF merger is completely free. You can combine unlimited PDF files without any registration, fees, or watermarks on your merged documents.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">How many PDFs can I merge at once?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">There's no limit! You can upload and merge as many PDF files as you need. Our tool handles multiple files efficiently, whether you're combining 2 or 20 documents.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Will the merged PDF maintain quality?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Absolutely! Our PDF merger preserves the original quality of all your documents. Text, images, formatting, and layout remain exactly as they were in the original files.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is it safe to merge PDFs online?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! We take security seriously. All files are processed over encrypted connections and automatically deleted from our servers after merging. Your documents remain private and confidential.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default MergePage;
